import { ErrorBoundary } from "@sentry/react";
import { createRootRouteWithContext, useMatches } from "@tanstack/react-router";
import { PropsWithChildren, Suspense } from "react";

import { RouterToast } from "@/components/router/toast";
import { RouterContext } from "@/router";

import { Layout } from "../components/layout";
import { LoadingScreen, SuspenseLoadingScreen } from "../components/loading-screen";
import { NavigationLoadingProvider } from "../providers/general/navigation-loading-provider";
import { ProtectedRoute } from "../providers/hooks/use-protected-route";
import { DashboardRoleProvider } from "./home/components/role-dashboard/hooks/useDashboardRole";
import { NotFoundPage as notFoundComponent } from "./not-found/-index";

export const Route = createRootRouteWithContext<RouterContext>()({
    component: Root,
    notFoundComponent,
});

function ProtectedRouteWrapper({ children }: PropsWithChildren) {
    const matches = useMatches();
    // Prefer the deepest route that declares permissions so nested pages inherit
    // parent gates when they omit their own, without overriding child-specific ones.
    let permissions: string[] = [];
    for (let i = matches.length - 1; i >= 0; i--) {
        const matchPermissions = matches[i]?.staticData?.permissions;
        if (Array.isArray(matchPermissions) && matchPermissions.length > 0) {
            permissions = matchPermissions;
            break;
        }
    }

    return <ProtectedRoute permissions={permissions}>{children}</ProtectedRoute>;
}

export function Root() {
    return (
        <ErrorBoundary>
            <DashboardRoleProvider>
                <ProtectedRouteWrapper>
                    <NavigationLoadingProvider>
                        <LoadingScreen />
                        <Suspense fallback={<SuspenseLoadingScreen />}>
                            <Layout />
                        </Suspense>
                    </NavigationLoadingProvider>
                </ProtectedRouteWrapper>
            </DashboardRoleProvider>

            <RouterToast />
        </ErrorBoundary>
    );
}
